/**
 * Recoverability API client (Spec 203 — US1 verification/score + US2 copy health)
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 203-recoverability-assurance
 */

import apiClient from '../services/api';

export type BackupEntityType = 'lakehouse' | 'warehouse';

/** Spec 246 US5 — the honest recoverability verdict (distinct from the run lifecycle status). */
export type RecoverabilityVerdict = 'recoverable' | 'degraded' | 'not_recoverable' | 'unverified' | 'refused';

export interface RecoverabilityScore {
  entityType: BackupEntityType;
  entityId: string;
  entityName: string | null;
  workspaceName: string | null;
  lastVerifiedAt: string | null;
  lastStatus: 'Running' | 'Passed' | 'Failed' | 'Skipped' | null;
  passRate: number | null;
  observedRtoSeconds: number | null;
  slaDays: number;
  atRisk: boolean;
  atRiskReason: string | null;
  totalVerifications: number;
  updatedAt: string | null;
  // Spec 246 US5 — honest metadata-proof state (null on pre-246 / restore-only scores).
  recoverabilityState?: RecoverabilityVerdict | null;
  integrityCeiling?: string | null; // 'countable-not-verified-correct'
  verifyMethod?: 'metadata' | 'restore' | 'inplace' | null;
  coherentCount?: number | null;
  tableCount?: number | null;
  /**
   * WHY it is not proven recoverable — `atRisk` alone put a real fault, an unfinished check and an
   * empty lakehouse in the same bucket, which is how the estate read "19 at risk" while holding one
   * finding. `null` means proven recoverable.
   */
  riskKind?: 'damaged' | 'incomplete' | 'unproven' | 'empty' | null;
  problemCount?: number | null;
  unresolvedCount?: number | null;
}

export interface VerificationRun {
  id: string;
  entityType: BackupEntityType;
  entityId: string;
  entityName: string | null;
  workspaceName: string | null;
  runId: string;
  status: 'Running' | 'Passed' | 'Failed' | 'Skipped';
  resultDetail: string | null;
  observedRtoSeconds: number | null;
  throwawayTarget: string | null;
  startedAt: string;
  completedAt: string | null;
  // Spec 246 US5 — honest verdict enrichment (null on legacy restore-based runs).
  verdict?: RecoverabilityVerdict | null;
  integrityCeiling?: string | null;
  method?: 'metadata' | 'restore' | 'inplace' | null;
  coherentCount?: number | null;
  tableCount?: number | null;
  problemCount?: number | null;
  unresolvedCount?: number | null;
  activeRecords?: number | null;
}

export interface VerifyNowResult {
  entityType: BackupEntityType;
  entityId: string;
  runId: string;
  status: 'running' | 'skipped';
  executionName: string | null;
  verificationRunId: string;
}

export interface VerificationPolicy {
  id: string;
  backupPlanId: string | null;
  enabled: boolean;
  cadenceCron: string;
  selection: 'newest' | 'oldest_newest' | 'sample_n' | 'all';
  sampleN: number | null;
  slaDays: number;
  maxConcurrent: number | null;
}

export interface RecoverabilitySummary {
  total: number;
  recoverable: number;
  atRisk: number;
  neverVerified: number;
  /** Null when no asset is in scope (Spec 298 FR-004). */
  verifiedPct: number | null;
  // Spec 246 US5 — honest verdict breakdown + the integrity ceiling for the tile copy.
  byVerdict?: {
    recoverable: number;
    degraded: number;
    notRecoverable: number;
    unverified: number;
    refused: number;
  };
  integrityCeiling?: string;
}

type Wrapped<T> = { success: boolean; data: T };
const BASE = '/api/v1/backup/recoverability';

export const recoverabilityApi = {
  summary: async (): Promise<RecoverabilitySummary> =>
    (await apiClient.get<Wrapped<RecoverabilitySummary>>(`${BASE}/summary`)).data,
  scores: async (): Promise<RecoverabilityScore[]> =>
    (await apiClient.get<Wrapped<{ scores: RecoverabilityScore[] }>>(`${BASE}/scores`)).data.scores,
  verifications: async (entityType?: BackupEntityType, entityId?: string): Promise<VerificationRun[]> => {
    const qs = new URLSearchParams();
    if (entityType) qs.set('entityType', entityType);
    if (entityId) qs.set('entityId', entityId);
    return (await apiClient.get<Wrapped<{ verifications: VerificationRun[] }>>(`${BASE}/verifications?${qs.toString()}`)).data.verifications;
  },
  policy: async (planId?: string): Promise<VerificationPolicy | null> =>
    (await apiClient.get<Wrapped<VerificationPolicy | null>>(`${BASE}/policy${planId ? `?planId=${planId}` : ''}`)).data,
  savePolicy: async (patch: Partial<VerificationPolicy> & { planId?: string | null }): Promise<VerificationPolicy> =>
    (await apiClient.put<Wrapped<VerificationPolicy>>(`${BASE}/policy`, patch)).data,
  verifyNow: async (entityType: BackupEntityType, entityId: string): Promise<VerifyNowResult> =>
    (await apiClient.post<Wrapped<VerifyNowResult>>(`${BASE}/verify/${entityType}/${entityId}`, {})).data,
  reconcile: async (): Promise<{ passed: number; failed: number; pending: number }> =>
    (await apiClient.post<Wrapped<{ passed: number; failed: number; pending: number }>>(`${BASE}/reconcile`, {})).data,
  // 🛑 Spec 270 FR-019a — `copyHealth`, `vaults`, `createVault` and `recoverByEntity` REMOVED,
  // together with the `CopyHealthRow` / `RecoveryVault` types and the `withSecondaryCopy` summary
  // field. Their eight backend routes are gone.
  //
  // 🛑 NEITHER TYPECHECK COULD HAVE CAUGHT THIS. `res.json()` is untyped on the backend and the
  // frontend declares its own shape, so the compiler is blind at the HTTP seam: leaving these in
  // place would have produced live 404s and an `undefined` rendered as a metric, with `tsc` green in
  // both projects. Found by grepping across the boundary, not by building.
};
